/**
 * URL model of the operator console. Pure: no React, no window access, so the
 * app router and the console share one definition.
 *
 *   /admin                            bare landing (narrow: section list)
 *   /admin/{section}                  one section, see ADMIN_SECTIONS
 *   /admin/users/{userId}[/{tab}]     one user, tab defaults to profile
 *   /admin/logs/{correlationId}       one open call
 *   /admin/logs?user={userId}         call list pre-filtered by a user
 *   /admin/audit[/{eventId}][?filters]   audit log, filters live in the query
 *   /admin/access[/operators[/{id}]]  operators (default view)
 *   /admin/access/roles[/{roleId}]    roles
 */
export const ADMIN_SECTIONS = [
  "overview",
  "load",
  "models",
  "inspector",
  "feedback",
  "users",
  "logs",
  "payments",
  "access",
  "audit",
] as const;

export type AdminSectionId = (typeof ADMIN_SECTIONS)[number];

export const USER_TABS = [
  "profile",
  "activity",
  "storage",
  "billing",
  "identities",
  "logs",
] as const;

export type UserTabId = (typeof USER_TABS)[number];

export const DEFAULT_USER_TAB: UserTabId = "profile";

export const AUDIT_PERIODS = ["24h", "7d", "30d", "all"] as const;
export type AuditPeriod = (typeof AUDIT_PERIODS)[number];
/** Period applied when the URL names none; left out of canonical hrefs. */
export const DEFAULT_AUDIT_PERIOD: AuditPeriod = "7d";

export const AUDIT_OUTCOMES = ["success", "denied", "failure"] as const;
export type AuditOutcome = (typeof AUDIT_OUTCOMES)[number];

export const ACCESS_VIEWS = ["operators", "roles"] as const;
export type AccessView = (typeof ACCESS_VIEWS)[number];

export interface AuditFilter {
  operator?: string;
  type?: string;
  user?: string;
  outcome?: AuditOutcome;
  /** Absent means the default period. */
  period?: AuditPeriod;
}

export interface AdminLocation {
  /** Absent on the bare /admin landing. */
  section?: AdminSectionId;
  userId?: string;
  userTab?: UserTabId;
  correlationId?: string;
  /** Pre-filter of the Logs section, from `?user=`. */
  logsUserId?: string;
  auditEventId?: string;
  auditFilter?: AuditFilter;
  accessView?: AccessView;
  operatorId?: string;
  roleId?: string;
}

export function isAdminSection(value: unknown): value is AdminSectionId {
  return ADMIN_SECTIONS.some((id) => id === value);
}

export function isUserTab(value: unknown): value is UserTabId {
  return USER_TABS.some((id) => id === value);
}

export function isAuditPeriod(value: unknown): value is AuditPeriod {
  return AUDIT_PERIODS.some((period) => period === value);
}

function isAuditOutcome(value: unknown): value is AuditOutcome {
  return AUDIT_OUTCOMES.some((outcome) => outcome === value);
}

function decode(segment: string): string | null {
  try {
    const value = decodeURIComponent(segment);
    return value === "" ? null : value;
  } catch {
    return null;
  }
}

function parseAccess(
  view: string | undefined,
  id: string | undefined,
): AdminLocation {
  if (view === undefined) return { section: "access", accessView: "operators" };
  if (!ACCESS_VIEWS.some((candidate) => candidate === view)) return {};
  const accessView = view as AccessView;
  if (id === undefined) return { section: "access", accessView };
  const decoded = decode(id);
  if (decoded === null) return {};
  return accessView === "operators"
    ? { section: "access", accessView, operatorId: decoded }
    : { section: "access", accessView, roleId: decoded };
}

function parseAuditFilter(search: string): AuditFilter {
  const params = new URLSearchParams(search);
  const operator = params.get("operator");
  const type = params.get("type");
  const user = params.get("user");
  const outcome = params.get("outcome");
  const period = params.get("period");
  return {
    ...(operator ? { operator } : {}),
    ...(type ? { type } : {}),
    ...(user ? { user } : {}),
    ...(isAuditOutcome(outcome) ? { outcome } : {}),
    // The default period is the absence of the field.
    ...(isAuditPeriod(period) && period !== DEFAULT_AUDIT_PERIOD
      ? { period }
      : {}),
  };
}

function parseAudit(eventSegment: string | undefined, search: string) {
  const auditFilter = parseAuditFilter(search);
  if (eventSegment === undefined)
    return { section: "audit" as const, auditFilter };
  const auditEventId = decode(eventSegment);
  return auditEventId === null
    ? {}
    : { section: "audit" as const, auditEventId, auditFilter };
}

/**
 * Parse the part of a location below /admin. `rest` are the path segments after
 * "admin"; anything unknown collapses to the bare landing.
 */
export function parseAdminLocation(
  rest: string[],
  search: string,
): AdminLocation {
  const [first, second, third, ...extra] = rest;
  if (first === undefined) return {};
  if (!isAdminSection(first) || extra.length > 0) return {};
  if (first === "users") {
    if (second === undefined) return { section: "users" };
    const userId = decode(second);
    if (userId === null) return {};
    if (third === undefined) {
      return { section: "users", userId, userTab: DEFAULT_USER_TAB };
    }
    return isUserTab(third) ? { section: "users", userId, userTab: third } : {};
  }
  if (first === "access") return parseAccess(second, third);
  if (third !== undefined) return {};
  if (first === "audit") return parseAudit(second, search);
  if (first === "logs") {
    if (second !== undefined) {
      const correlationId = decode(second);
      return correlationId === null ? {} : { section: "logs", correlationId };
    }
    const logsUserId = new URLSearchParams(search).get("user");
    return logsUserId ? { section: "logs", logsUserId } : { section: "logs" };
  }
  return second === undefined ? { section: first } : {};
}

function auditHref(location: AdminLocation): string {
  const { auditEventId, auditFilter } = location;
  const base =
    auditEventId === undefined
      ? "/admin/audit"
      : `/admin/audit/${encodeURIComponent(auditEventId)}`;
  const params = new URLSearchParams();
  if (auditFilter?.operator) params.set("operator", auditFilter.operator);
  if (auditFilter?.type) params.set("type", auditFilter.type);
  if (auditFilter?.user) params.set("user", auditFilter.user);
  if (auditFilter?.outcome) params.set("outcome", auditFilter.outcome);
  if (auditFilter?.period && auditFilter.period !== DEFAULT_AUDIT_PERIOD) {
    params.set("period", auditFilter.period);
  }
  const query = params.toString();
  return query === "" ? base : `${base}?${query}`;
}

function accessHref(location: AdminLocation): string {
  const { accessView, operatorId, roleId } = location;
  if (accessView === "roles") {
    return roleId === undefined
      ? "/admin/access/roles"
      : `/admin/access/roles/${encodeURIComponent(roleId)}`;
  }
  return operatorId === undefined
    ? "/admin/access"
    : `/admin/access/operators/${encodeURIComponent(operatorId)}`;
}

/** Canonical href; the default user tab is left out of the URL. */
export function adminHref(location: AdminLocation): string {
  const { section, userId, userTab, correlationId, logsUserId } = location;
  if (section === undefined) return "/admin";
  if (section === "audit") return auditHref(location);
  if (section === "access") return accessHref(location);
  if (section === "users" && userId !== undefined) {
    const base = `/admin/users/${encodeURIComponent(userId)}`;
    return userTab && userTab !== DEFAULT_USER_TAB
      ? `${base}/${userTab}`
      : base;
  }
  if (section === "logs") {
    if (correlationId !== undefined) {
      return `/admin/logs/${encodeURIComponent(correlationId)}`;
    }
    if (logsUserId) {
      return `/admin/logs?user=${encodeURIComponent(logsUserId)}`;
    }
  }
  return `/admin/${section}`;
}

/** Split "/path?query#hash" into its pathname segments and search string. */
export function splitPath(path: string): { parts: string[]; search: string } {
  const withoutHash = path.split("#")[0] ?? "";
  const queryAt = withoutHash.indexOf("?");
  const pathname = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
  const search = queryAt === -1 ? "" : withoutHash.slice(queryAt);
  return { parts: pathname.split("/").filter(Boolean), search };
}
